import { MouseEvent, ReactNode, useState, ReactElement } from 'react';

import InfoIconOutlined from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

import { ClickAwayListener } from '@mui/material';
import useResponsive from 'hooks/useResponsive';

interface CustomTooltipProps {
  title: ReactNode | string;
  placement?: TooltipProps['placement'];
  color?: string;
  dataTestId?: string;
  children?: ReactElement;
  fullWidthWrapper?: boolean;
}

const CustomTooltip = ({
  title = '',
  placement = 'top',
  color = 'grey.800',
  dataTestId = 'custom-tooltip',
  children,
  fullWidthWrapper = false,
}: CustomTooltipProps) => {
  const isTablet = useResponsive('down', 'md');

  const [isTooltipOpen, setIsToolTipOpen] = useState<boolean>(false);

  const handleTooltipClose = () => {
    setIsToolTipOpen(false);
  };

  const handleTooltipOpen = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsToolTipOpen(true);
  };

  // If children are provided, wrap them; otherwise use default icon behavior
  const getTooltipContent = (): ReactElement => {
    if (children) {
      if (isTablet) {
        return (
          <Box
            component="span"
            onClick={handleTooltipOpen}
            sx={{
              display: fullWidthWrapper ? 'flex' : 'inline-block',
              cursor: 'pointer',
              ...(fullWidthWrapper && { justifyContent: 'center' }),
            }}
            data-testid={`${dataTestId}-wrapper`}
          >
            {children}
          </Box>
        );
      }
      return children;
    }

    if (isTablet) {
      return (
        <IconButton onClick={handleTooltipOpen} sx={{ py: 0 }}>
          <InfoIconOutlined fontSize="small" sx={{ color }} />
        </IconButton>
      );
    }

    return <InfoIconOutlined fontSize="small" sx={{ cursor: 'pointer', color, ml: 0.3 }} />;
  };

  const tooltipContent: ReactElement = getTooltipContent();

  return isTablet ? (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div style={{ ...(fullWidthWrapper && { width: '100%' }) }}>
        <Tooltip
          title={title}
          placement="top"
          onClose={handleTooltipClose}
          open={isTooltipOpen}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          data-testid={dataTestId}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: '27ch',
              },
            },
            popper: {
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                    tether: false,
                  },
                },
              ],
              sx: {
                zIndex: 1000,
              },
              disablePortal: true,
            },
          }}
        >
          {tooltipContent}
        </Tooltip>
      </div>
    </ClickAwayListener>
  ) : (
    <Tooltip
      title={title}
      placement={placement}
      enterTouchDelay={0}
      leaveTouchDelay={5000}
      data-testid={dataTestId}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: '40ch',
          },
        },
      }}
    >
      {tooltipContent}
    </Tooltip>
  );
};

export default CustomTooltip;
